import './css/style.scss';
import './i18n/i18n';

import { b2x } from '@b2x/react/src';
import _ from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom/client';

import { App } from './App';
import { Brand, BrandHostname, hostnameToBrandMap } from './AppContext';
import { BrandSelector } from './BrandSelector';
import { Button, ButtonVariant } from './Button';
import { Container } from './Container';
import { en } from './i18n/locales/en';
import { enGoldbet } from './i18n/locales/en-goldbet';
import { enLottomatica } from './i18n/locales/en-lottomatica';
import { enLottomaticaservizi } from './i18n/locales/en-lottomaticaservizi';
import { enPlanetwin365 } from './i18n/locales/en-planetwin365';
import { it } from './i18n/locales/it';
import { itGoldbet } from './i18n/locales/it-goldbet';
import { itLottomatica } from './i18n/locales/it-lottomatica';
import { itLottomaticaservizi } from './i18n/locales/it-lottomaticaservizi';
import { itPlanetwin365 } from './i18n/locales/it-planetwin365';
import { IconName, IconSize } from './Icon';
import { getShopJsonLdBrand, getShopName } from './pages/StoreLocatorPage';
import { ThemeColor } from './pages/TemplatePage';
import { reportWebVitals } from './reportWebVitals';

let brand: Brand | undefined;

const hostname = window.location.hostname;
const reverseProxyHostname = Object.keys(hostnameToBrandMap).find((_hostname) => hostname.includes(_hostname)) as
  | BrandHostname
  | undefined;
const isReverseProxy = reverseProxyHostname !== undefined;

if (reverseProxyHostname) {
  // Se sono sotto reverseProxy, deduco il brand dall'hostname.
  brand = hostnameToBrandMap[reverseProxyHostname];
} else {
  // Se non sono sotto reverseProxy...
  // ...vedo se ho salvato il brand nel session starage
  const brandFromSessionStorage = window.sessionStorage.getItem('brand') ?? undefined;
  if (brandFromSessionStorage) {
    brand = Object.values(hostnameToBrandMap).find((_brand) => _brand === brandFromSessionStorage);
  }

  if (!brand) {
    // altrimenti lo deduco dal queryParam, lo salvo nel session starage e riavvio l'app.
    const urlSearchParams = new URLSearchParams(window.location.search);
    const brandFromQueryParam = urlSearchParams.get('brand') ?? undefined;
    brand = Object.values(hostnameToBrandMap).find((_brand) => _brand === brandFromQueryParam);

    if (brand) {
      window.sessionStorage.setItem('brand', brand);
      // eslint-disable-next-line node/no-process-env
      window.location.href = process.env.PUBLIC_URL === '' ? '/' : process.env.PUBLIC_URL;
    }
  }
}

const getGTMId = (currentBrand: Brand | undefined) => {
  if (currentBrand === 'lottomatica') {
    return 'GTM-53RTPGQ';
  } else if (currentBrand === 'goldbet') {
    return 'GTM-W9BNNJC';
  } else if (currentBrand === 'planetwin365') {
    return 'GTM-T8PB9KSK';
  } else if (currentBrand === 'lottomaticaservizi') {
    // return 'GTM-MMXPDQX';
    return 'GTM-T5KLQHXG';
  }
};

const getGoogleMapsApiKey = (currentBrand: Brand) => {
  if (currentBrand === 'planetwin365') {
    return 'AIzaSyDmYIEOyZeYmg-_7wMSEOG7WRcic4WSX6g';
  } else if (currentBrand === 'lottomaticaservizi') {
    return 'AIzaSyAatcMNmWht70R__O4KF_6tT7aj5cvi2YA';
  }
};

const geti18n = (currentBrand: Brand | undefined) => {
  if (currentBrand === 'lottomatica') {
    return {
      en: _.merge(en, enLottomatica),
      it: _.merge(it, itLottomatica),
    };
  } else if (currentBrand === 'goldbet') {
    return {
      en: _.merge(en, enGoldbet),
      it: _.merge(it, itGoldbet),
    };
  } else if (currentBrand === 'planetwin365') {
    return {
      en: _.merge(en, enPlanetwin365),
      it: _.merge(it, itPlanetwin365),
    };
  } else if (currentBrand === 'lottomaticaservizi') {
    return {
      en: _.merge(en, enLottomaticaservizi),
      it: _.merge(it, itLottomaticaservizi),
    };
  }

  return { en, it };
};

const getOneTrust = (currentBrand: Brand) => {
  let domainScript = undefined;
  if (currentBrand === 'lottomatica') {
    domainScript = 'd30fc64e-3a4c-4a32-b3d2-c2e21b595705';
  }
  if (currentBrand === 'goldbet') {
    domainScript = '19534b10-19e2-463b-8389-2cb3da39d14f';
  }

  if (currentBrand === 'planetwin365') {
    domainScript = '249e985c-66b8-4ae7-87e6-4a0906d0b456';
  }

  /*   if (currentBrand === 'lottomaticaservizi' && b2x.environment === 'TEST') {
    domainScript = '019e6356-c753-774b-9e80-31e7dfaaf1c7-test';
  }
 */
  if (currentBrand === 'lottomaticaservizi') {
    domainScript = '019e2ac4-f2f5-7836-8a3f-131b9c6b91f6';
  }

  return domainScript ? { domainScript } : undefined;
};

/* const getCookiebot = (currentBrand: Brand) => {
  let cookiebotId = undefined;
  if (currentBrand === 'lottomaticaservizi') {
    cookiebotId = '83be752f-33fe-49d5-b410-29db6200a262';
  }

  return cookiebotId ? { id: cookiebotId } : undefined;
}; */

if (brand) {
  // Costante per mantenere il narrowing di brand dentro le closure di init.
  const siteBrand = brand;
  const googleMapsApiKey = getGoogleMapsApiKey(brand);
  b2x.init<ThemeColor, ButtonVariant, IconName, IconSize>({
    analytics:
      b2x.environment === 'PRODUCTION'
        ? {
            googleTagManager: {
              id: getGTMId(brand),
            },
          }
        : undefined,
    // Non so bene perché, ma gtm di lottomatica da un errore se lanciato in localhost:3000, lo levo.
    api: {
      apiKey: 'GHHFGJDAABGIJGADHFFCCG', // Lottomatica
    },
    //cookiebot: getCookiebot(brand),
    defaultButtonVariant: 'primary',

    form: {
      buttons: {
        cancel: {
          defaultVariant: 'outline-secondary',
        },
      },
    },

    googleMaps:
      googleMapsApiKey !== undefined
        ? {
            apiKey: googleMapsApiKey,
          }
        : undefined,
    i18n: geti18n(brand),
    icons: {
      close: 'closed',
      configurator: 'closed',
      download: 'closed',
      logout: 'closed',
      share: 'share',
    },
    oneTrust: getOneTrust(brand),
    preRelease: true,
    projectName: 'lottomatica',
    shopJsonLdBrandResolver: (shop) => getShopJsonLdBrand(shop, siteBrand),
    shopJsonLdGraph: true,
    shopNameResolver: (shop) => getShopName(shop),
    ui: 'bs5',
    variants: {
      Button: Button,
      Container: Container,
    },
  });
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    {brand ? (
      <b2x.router.BrowserRouter>
        <b2x.ModalsContext>
          <App brand={brand} reverseProxy={isReverseProxy} />
        </b2x.ModalsContext>
      </b2x.router.BrowserRouter>
    ) : !isReverseProxy ? (
      <BrandSelector />
    ) : (
      <></>
    )}
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
